/**
 * Reply quoting for issue comments (WHA-3810). Replies are plain comments
 * whose body opens with the quoted parent as `> …` lines plus an `@mention`
 * of its author — so replying needs no comment-thread schema, and every
 * existing surface (search, notifications, edit) keeps working unchanged.
 *
 * Only a LEADING `> …` run is treated as a reply quote — that's the shape the
 * composer produces. `>` lines elsewhere in a body are user-authored text and
 * are left alone (rendered plain, kept when quoted).
 */

/** Longest stretch of an original comment carried into a reply's quote. */
export const QUOTE_MAX_CHARS = 240;

/** Index of the first line past the leading `> …` run (0 when there is none). */
function leadingQuoteEnd(lines: string[]): number {
  let end = 0;
  while (end < lines.length && lines[end].startsWith(">")) end++;
  return end;
}

/**
 * The plain text a reply quotes from its parent comment — no `>` markers.
 * The composer holds this as a separate, non-editable block (WHA-3810 round
 * 3), so a replying user cannot rewrite the quoted words before sending.
 */
export function quoteTextOf(body: string): string {
  // Replying to a reply: drop only the parent's leading reply-quote block.
  // Interior `>` lines are the parent author's own text and stay — they come
  // out as `> > …`, which splitQuoteRuns renders as a literal `> ` inside the
  // blockquote, reading as the nested quote it is.
  const lines = body.split("\n");
  const withoutQuotes = lines.slice(leadingQuoteEnd(lines)).join("\n");
  const trimmed = (withoutQuotes.trim() || body).trim();
  return trimmed.length > QUOTE_MAX_CHARS
    ? `${trimmed.slice(0, QUOTE_MAX_CHARS).trimEnd()}…`
    : trimmed;
}

/** Wire format of a quote block: every line prefixed with `> `. */
export function prefixQuoteLines(text: string): string {
  return text
    .split("\n")
    .map((line) => `> ${line}`)
    .join("\n");
}

/** Build the `> …` block a reply quotes its parent comment with. */
export function quoteOf(body: string): string {
  return prefixQuoteLines(quoteTextOf(body));
}

/**
 * Merge the composer's read-only quote block with the text the user actually
 * typed, producing the body that goes on the wire. The quote is re-derived
 * here from composer state — never from the textarea — so the sent comment
 * quotes the original words verbatim.
 */
export function composeReplyBody(quote: string | null, typed: string): string {
  const trimmed = typed.trim();
  if (!quote) return trimmed;
  return `${prefixQuoteLines(quote)}\n${trimmed}`.trim();
}

export interface CommentRun {
  quoted: boolean;
  text: string;
}

/**
 * Split a comment body into the leading reply-quote run (if any) and the rest,
 * so a reply's `> …` header renders as a quote block. `>` lines after the
 * first plain line are user-authored and stay in the plain run untouched.
 */
export function splitQuoteRuns(body: string): CommentRun[] {
  const lines = body.split("\n");
  const quoteEnd = leadingQuoteEnd(lines);
  if (quoteEnd === 0) return [{ quoted: false, text: body }];
  const runs: CommentRun[] = [
    {
      quoted: true,
      text: lines
        .slice(0, quoteEnd)
        .map((line) => line.replace(/^>\s?/, ""))
        .join("\n"),
    },
  ];
  if (quoteEnd < lines.length) {
    runs.push({ quoted: false, text: lines.slice(quoteEnd).join("\n") });
  }
  return runs;
}
